import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchTrashedItems, restoreItem, purgeItem } from '../services/api';
import type { DriveItem } from '../types';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { Thumbnail } from '../components/TypeGlyph';
import { useAppStore } from '../store/useAppStore';

function relativeTime(dateStr?: string): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function Trash() {
  const { toast } = useAppStore();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<DriveItem | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: qk.trash(),
    queryFn: fetchTrashedItems,
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.trash() }); qc.invalidateQueries({ queryKey: ['items'] }); toast('File restored'); },
  });

  const purgeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => purgeItem(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.trash() }); setPurgeTarget(null); toast('File permanently deleted'); },
  });

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  }

  const selArr = [...selected];
  const selItems = items.filter(i => selected.has(i.id));
  const heldSel = selItems.filter(i => i.hold_active).length;

  async function bulkRestore() {
    for (const id of selArr) {
      await restoreItem(id).catch(() => {});
    }
    qc.invalidateQueries({ queryKey: qk.trash() });
    qc.invalidateQueries({ queryKey: ['items'] });
    setSelected(new Set());
    toast(`${selArr.length} file${selArr.length > 1 ? 's' : ''} restored`);
  }

  async function bulkPurge() {
    const purgeable = selItems.filter(i => !i.hold_active);
    for (const item of purgeable) {
      await purgeItem(item.id).catch(() => {});
    }
    qc.invalidateQueries({ queryKey: qk.trash() });
    setSelected(new Set());
    toast(`${purgeable.length} file${purgeable.length > 1 ? 's' : ''} permanently deleted`);
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Trash</h2>
        {items.length > 0 && (
          <Button kind="danger" icon="trash" onClick={() => setShowEmptyConfirm(true)}>Empty trash</Button>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map(i => <div key={i} className="t-skeleton" style={{ height: 60, borderRadius: 8 }} />)}
          </div>
        ) : items.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, color: 'var(--ink-3)' }}>
            <Icon name="trash" size={40} color="var(--ink-4)" />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>Trash is empty</div>
            <div style={{ fontSize: 13 }}>Files you delete will appear here.</div>
          </div>
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                <th style={{ width: 36, paddingLeft: 16 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === items.length && items.length > 0}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < items.length; }}
                    onChange={toggleAll}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                  />
                </th>
                <th>Name</th>
                <th>Trashed</th>
                <th>Size</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className={selected.has(item.id) ? 'is-selected' : ''}>
                  <td style={{ paddingLeft: 16 }}>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Thumbnail item={item} width={28} height={28} />
                      <span style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{item.name}</span>
                      {item.hold_active && <Chip tone="warn" size="sm">Hold</Chip>}
                    </div>
                  </td>
                  <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{relativeTime(item.trashed_at)}</td>
                  <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }}>{formatBytes(Number(item.currentVersion?.size_bytes ?? item.size_bytes ?? 0))}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button kind="outline" size="xs" icon="refresh" onClick={() => restoreMutation.mutate(item.id)} loading={restoreMutation.isPending}>Restore</Button>
                      <Button
                        kind="danger"
                        size="xs"
                        icon="trash"
                        disabled={item.hold_active}
                        onClick={() => setPurgeTarget(item)}
                      >
                        Purge
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Bulk action bar */}
      {selArr.length >= 1 && (
        <div className="t-bulkbar">
          <span style={{ fontWeight: 600 }}>{selArr.length} selected</span>
          {heldSel > 0 && <Chip tone="warn">{heldSel} held (skipped on purge)</Chip>}
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)' }} />
          <Button kind="ghost" size="sm" icon="refresh" style={{ color: 'white' }} onClick={bulkRestore}>
            Restore
          </Button>
          <Button kind="ghost" size="sm" icon="trash" style={{ color: 'rgba(255,100,80,0.9)' }} onClick={bulkPurge}>
            Delete permanently
          </Button>
          <Button kind="ghost" size="sm" icon="x" iconOnly style={{ color: 'white' }} onClick={() => setSelected(new Set())} />
        </div>
      )}

      {/* Empty trash confirm */}
      <Modal
        open={showEmptyConfirm}
        onClose={() => setShowEmptyConfirm(false)}
        title="Empty trash"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowEmptyConfirm(false)}>Cancel</Button>
            <Button
              kind="danger"
              onClick={() => {
                items.filter(i => !i.hold_active).forEach(i => purgeMutation.mutate({ id: i.id }));
                setShowEmptyConfirm(false);
                toast('Trash emptied');
              }}
            >
              Empty trash
            </Button>
          </>
        }
      >
        <div className="t-stack">
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
            This will permanently delete all {items.filter(i => !i.hold_active).length} files not under a retention hold. This cannot be undone.
          </p>
          {items.some(i => i.hold_active) && (
            <div className="t-alert t-alert--warn">
              <Icon name="warn" size={14} />
              {items.filter(i => i.hold_active).length} files under hold will be skipped.
            </div>
          )}
        </div>
      </Modal>

      {/* Purge single file */}
      {purgeTarget && (
        <Modal
          open
          onClose={() => setPurgeTarget(null)}
          title="Permanently delete"
          size="sm"
          footer={
            <>
              <Button kind="ghost" onClick={() => setPurgeTarget(null)}>Cancel</Button>
              <Button
                kind="danger"
                loading={purgeMutation.isPending}
                onClick={() => purgeMutation.mutate({ id: purgeTarget.id })}
              >
                Delete permanently
              </Button>
            </>
          }
        >
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
            <strong>{purgeTarget.name}</strong> will be permanently deleted. This cannot be undone.
          </p>
        </Modal>
      )}
    </div>
  );
}
